// @flow

import { observer } from "mobx-react";
import React from "react";

import TextFormatter from "app/util/TextFormatter";

import "./VideoProgress.scss";

type Props = {
    progress: number,
    buffered?: number,
    duration: number,
    onSeek?: Function,
    times?: boolean,
    pointer?: boolean,
    hoverProgress?: ?number,
    hoverEnabled?: boolean,
    hide?: boolean
};

type State = {
    hoverProgress: ?number
};

@observer
export default class VideoProgress extends React.Component<Props, State> {
    bar: ?HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = { hoverProgress: null };
    }

    getHoverProgress() {
        return this.props.hoverProgress || this.state.hoverProgress;
    }

    getProgressFromMouseEvent(event: SyntheticMouseEvent<>) {
        if (!this.bar) return;

        const element = this.bar;
        const distance = event.pageX - element.getBoundingClientRect().left;
        return (this.props.duration * distance) / element.offsetWidth;
    }

    handleSeek = (event: SyntheticMouseEvent<HTMLDivElement>) => {
        const onSeek = this.props.onSeek;
        if (!onSeek) return null;

        onSeek(this.getProgressFromMouseEvent(event));
    };

    handleMouseMove = (event: SyntheticMouseEvent<HTMLDivElement>) => {
        if (!this.props.times) return null;

        this.setState({ hoverProgress: this.getProgressFromMouseEvent(event) });
    };

    handleMouseOut = () => {
        this.setState({ hoverProgress: null });
    };

    percentage(time: number) {
        return Math.min(100, (time * 100) / this.props.duration);
    }

    render() {
        if (!this.props.duration) return null;

        const progressPercentage = this.percentage(this.props.progress);

        let barsProps = {};
        if (this.props.progress) {
            barsProps = {
                onMouseMove: this.handleMouseMove,
                onMouseOut: this.handleMouseOut
            };
        }

        return (
            <div
                ref={element => (this.bar = element)}
                className="VideoProgress"
                onClick={this.handleSeek}
            >
                {this.renderTimes()}
                <div className="VideoProgress__bars" {...barsProps}>
                    <div className="VideoProgress__background" />
                    {this.renderHover()}
                    {this.renderBuffer()}
                    <div
                        className="VideoProgress__progress"
                        style={{ width: `${progressPercentage}%` }}
                    />
                    {this.renderPointer()}
                </div>
            </div>
        );
    }

    renderHover() {
        const hoverProgress = this.getHoverProgress();
        if (!hoverProgress) return null;

        const hoverPercentage = this.percentage(hoverProgress);
        return (
            <div
                className="VideoProgress__hover"
                style={{ width: `${hoverPercentage}%` }}
            />
        );
    }

    renderBuffer() {
        if (!this.props.buffered) return null;

        const bufferedPercentage = this.percentage(this.props.buffered);
        return (
            <div
                className="VideoProgress__buffer"
                style={{ width: `${bufferedPercentage}%` }}
            />
        );
    }

    renderTimes() {
        if (!this.props.times) return null;

        const progress = this.props.progress;
        const hoverProgress = this.getHoverProgress();

        const classes = ["VideoProgress__times"];

        return (
            <div className={classes.join(" ")}>
                {this.renderTime("current", progress)}
                {hoverProgress && this.renderTime("hover", hoverProgress)}
            </div>
        );
    }

    renderTime(name: string, progress: number) {
        if (progress === 0) return null;

        const left = this.percentage(progress);

        const classes = ["VideoProgress__time", `VideoProgress__time--${name}`];
        if (this.props.hide) {
            classes.push("VideoProgress__time--hide");
        }

        return (
            <span className={classes.join(" ")} style={{ left: `${left}%` }}>
                {TextFormatter.seconds(progress, true)}
            </span>
        );
    }

    renderPointer() {
        if (!this.props.pointer) return null;

        const progressPercentage = this.percentage(this.props.progress);

        return (
            <div className="VideoProgress__pointer">
                <div
                    className="VideoProgress__pointer-square"
                    style={{ left: `${progressPercentage}%` }}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/VideoProgress/VideoProgress.js